import { useEffect, useRef, useState, useCallback } from 'react';
// MediaPipe is lazy-loaded inside `initialize()` below — its 2MB SDK should
// NOT be in the main bundle. Static import here used to bloat every page load.

export const POSE_LANDMARKS = {
  NOSE: 0,
  LEFT_EYE_INNER: 1, LEFT_EYE: 2, LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4, RIGHT_EYE: 5, RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7, RIGHT_EAR: 8,
  MOUTH_LEFT: 9, MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
  LEFT_WRIST: 15, RIGHT_WRIST: 16,
  LEFT_PINKY: 17, RIGHT_PINKY: 18,
  LEFT_INDEX: 19, RIGHT_INDEX: 20,
  LEFT_THUMB: 21, RIGHT_THUMB: 22,
  LEFT_HIP: 23, RIGHT_HIP: 24,
  LEFT_KNEE: 25, RIGHT_KNEE: 26,
  LEFT_ANKLE: 27, RIGHT_ANKLE: 28,
  LEFT_HEEL: 29, RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31, RIGHT_FOOT_INDEX: 32
};

export const POSE_CONNECTIONS = [
  [11, 12], [11, 23], [12, 24], [23, 24],
  [11, 13], [13, 15], [12, 14], [14, 16],
  [23, 25], [25, 27], [24, 26], [26, 28],
  [27, 29], [27, 31], [28, 30], [28, 32],
];

// ===================== VISIBILITY =====================
const MIN_VIS = 0.7;            // 2D visibility threshold (used for general gating)
const MIN_VIS_3D = 0.85;        // Tighter threshold when we'll use the 3D world coord
                                // (MediaPipe's z-axis is much noisier than x/y)

const isVisible = (lm, idx) => {
  if (!lm || !lm[idx]) return false;
  return (lm[idx].visibility || 0) >= MIN_VIS;
};

const isVisible3D = (lm, idx) => {
  if (!lm || !lm[idx]) return false;
  return (lm[idx].visibility || 0) >= MIN_VIS_3D;
};

const checkBodyVisibility = (lm) => {
  if (!lm || lm.length < 33) return { head: false, shoulders: false, hips: false, knees: false, ankles: false, fullBody: false, upperBody: false, lowerBody: false };
  const head = isVisible(lm, 0);
  const shoulders = isVisible(lm, 11) && isVisible(lm, 12);
  const hips = isVisible(lm, 23) && isVisible(lm, 24);
  const knees = isVisible(lm, 25) && isVisible(lm, 26);
  const ankles = isVisible(lm, 27) && isVisible(lm, 28);
  const upperBody = shoulders && hips;
  const lowerBody = hips && knees && ankles;
  const fullBody = upperBody && lowerBody;
  return { head, shoulders, hips, knees, ankles, fullBody, upperBody, lowerBody };
};

const safeAngle = (lm, idxA, idxB, idxC, calcFn) => {
  if (!isVisible(lm, idxA) || !isVisible(lm, idxB) || !isVisible(lm, idxC)) return null;
  return calcFn(lm[idxA], lm[idxB], lm[idxC]);
};

// Per-landmark depth (z) stability buffer. Last 5 z-values per landmark idx.
// If z swings more than DEPTH_JUMP_THRESHOLD frame-to-frame, the 3D coord is
// untrustworthy (MediaPipe BlazePose's z is noisy at angled views and self-
// occlusion). Used to gate 3D-derived angles.
const _depthBuf = {};
const DEPTH_BUF_SIZE = 5;
const DEPTH_JUMP_THRESHOLD = 0.15;   // world-coord units; ~7° at typical limb scale

const isDepthStable = (lm3D, idx) => {
  if (!lm3D || !lm3D[idx]) return false;
  const z = lm3D[idx].z;
  if (z === undefined || isNaN(z)) return false;
  const buf = _depthBuf[idx] || (_depthBuf[idx] = []);
  buf.push(z);
  if (buf.length > DEPTH_BUF_SIZE) buf.shift();
  if (buf.length < 2) return true;       // need history; assume good first
  // Reject if any frame-to-frame swing exceeds threshold
  for (let i = 1; i < buf.length; i++) {
    if (Math.abs(buf[i] - buf[i - 1]) > DEPTH_JUMP_THRESHOLD) return false;
  }
  return true;
};

// 3D angle: tighter visibility check (0.85) AND depth-stability gate.
// Returns null when the 3D world coords are too noisy to trust — caller falls
// back to alt-side or gives up. This is the primary fix for the angled-camera
// 33° artifact.
const safeAngle3D = (lm2D, lm3D, idxA, idxB, idxC) => {
  if (!isVisible3D(lm2D, idxA) || !isVisible3D(lm2D, idxB) || !isVisible3D(lm2D, idxC)) return null;
  if (!lm3D || !lm3D[idxA] || !lm3D[idxB] || !lm3D[idxC]) return null;
  if (!isDepthStable(lm3D, idxA) || !isDepthStable(lm3D, idxB) || !isDepthStable(lm3D, idxC)) return null;
  return calcAngle3D(lm3D[idxA], lm3D[idxB], lm3D[idxC]);
};

// Median of an array of numbers — used for robust per-frame angle smoothing
// (replaces moving average so a single bad frame can't poison the result).
const median = (arr) => {
  if (!arr || arr.length === 0) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// =============================================================================
// FRONT-VIEW METRIC CALCULATORS (Phase A.7 — view-aware measurement)
// =============================================================================
// These metrics use only 2D image-space coordinates (x, y) so they're immune
// to the MediaPipe 3D depth ambiguity that ruins 3D angles from the front.
// Use ONLY when cameraView === 'front'. They become invalid (or trivial) from
// the side, where 3D angles take over.

// Symmetry: 100 = perfectly symmetric, 0 = max asymmetry. Compares Y-coords of
// L vs R landmarks (e.g. shoulder L=11 / R=12, hip L=23 / R=24, elbow L=13 / R=14).
const calcSymmetryY = (lm, leftIdx, rightIdx) => {
  if (!lm || !lm[leftIdx] || !lm[rightIdx]) return null;
  if (!isVisible(lm, leftIdx) || !isVisible(lm, rightIdx)) return null;
  const dy = Math.abs(lm[leftIdx].y - lm[rightIdx].y);
  // 0.0 → 100, 0.10 → 0. Image-space y is normalized [0,1].
  return Math.max(0, Math.min(100, Math.round((1 - dy / 0.10) * 100)));
};

// Elbow flare (pushups, front view): angle between the upper-arm vector and
// the torso vertical line, projected to image plane (x,y only).
//  - 0° = arm tucked tight to torso (good pushup form)
//  - 90° = arm horizontal/perpendicular (full T-flare, bad pushup form)
// Returns degrees from torso vertical (NOT a "good = 100" score — that's the
// scorer's job).
const calcElbowFlare = (lm, shoulderIdx, elbowIdx) => {
  if (!lm || !lm[shoulderIdx] || !lm[elbowIdx]) return null;
  if (!isVisible(lm, shoulderIdx) || !isVisible(lm, elbowIdx)) return null;
  const dx = lm[elbowIdx].x - lm[shoulderIdx].x;
  const dy = lm[elbowIdx].y - lm[shoulderIdx].y;
  // angle from vertical (down direction is +y in image space)
  const angle = Math.atan2(Math.abs(dx), Math.abs(dy)) * 180 / Math.PI;
  return Math.round(angle);
};

// Shoulder tilt: tilt of the shoulder line in the image plane, in degrees.
//  - 0° = horizontal (good)
//  - 10°+ = noticeably uneven
const calcShoulderTilt = (lm) => {
  if (!lm || !lm[11] || !lm[12]) return null;
  if (!isVisible(lm, 11) || !isVisible(lm, 12)) return null;
  const dx = lm[12].x - lm[11].x;
  const dy = lm[12].y - lm[11].y;
  return Math.round(Math.atan2(dy, Math.abs(dx)) * 180 / Math.PI);
};

// Knee valgus (squats, front view): ratio of horizontal knee-spread to ankle-
// spread. 1.0 = knees track ankles (good). <0.85 = knees caving inward (bad).
// Output: 0–100 score where 100 = aligned, drops as knees cave or splay.
const calcKneeValgus = (lm) => {
  if (!lm) return null;
  if (!isVisible(lm, 25) || !isVisible(lm, 26) || !isVisible(lm, 27) || !isVisible(lm, 28)) return null;
  const kneeSpread = Math.abs(lm[25].x - lm[26].x);
  const ankleSpread = Math.abs(lm[27].x - lm[28].x);
  if (ankleSpread < 0.02) return null;   // not enough spread to judge
  const ratio = kneeSpread / ankleSpread;
  // 1.0 → 100, 0.55 or 1.45 → 0. Penalize both caving and splaying, but
  // tolerant of the natural slight knee-in / stance variance and the skew that
  // creeps in when the camera isn't perfectly square-on. (Was 0.30 = far too
  // harsh — a healthy squat 7% off scored 75 / "out of range".)
  const dist = Math.abs(ratio - 1.0);
  return Math.max(0, Math.min(100, Math.round((1 - dist / 0.45) * 100)));
};

// Head alignment: how vertical the nose-shoulder-hip line is in image plane.
//  - 0° = perfectly stacked (good)
//  - 20°+ = head dropping forward / leaning
const calcHeadAlignment = (lm) => {
  if (!lm || !lm[0] || !lm[11] || !lm[12] || !lm[23] || !lm[24]) return null;
  if (!isVisible(lm, 0)) return null;
  if (!isVisible(lm, 11) || !isVisible(lm, 12)) return null;
  if (!isVisible(lm, 23) || !isVisible(lm, 24)) return null;
  const shY = (lm[11].y + lm[12].y) / 2;
  const shX = (lm[11].x + lm[12].x) / 2;
  const hipY = (lm[23].y + lm[24].y) / 2;
  const hipX = (lm[23].x + lm[24].x) / 2;
  // Angle of the nose-to-shoulder vector vs torso vector
  const torsoDx = shX - hipX;
  const torsoDy = shY - hipY;
  const torsoLen = Math.hypot(torsoDx, torsoDy);
  if (torsoLen < 0.05) return null;
  const headDx = lm[0].x - shX;
  const headDy = lm[0].y - shY;
  const headLen = Math.hypot(headDx, headDy);
  if (headLen < 0.02) return null;
  // Cross product / dot to get angle between vectors
  const dot = (torsoDx * headDx + torsoDy * headDy) / (torsoLen * headLen);
  const angle = Math.acos(Math.max(-1, Math.min(1, dot))) * 180 / Math.PI;
  // 180° = fully aligned (head extends torso line). 90° = head perpendicular.
  return Math.round(180 - angle);
};

// ===================== SKELETON SANITY CHECK =====================
// MediaPipe occasionally returns inverted / scrambled skeletons at distance or during fast motion.
// Reject frames where body orientation violates anatomy.
// Image-space Y: 0 = top of frame, 1 = bottom. So "above" in the image = smaller Y.
const isSkeletonSane = (lm) => {
  if (!lm || lm.length < 33) return false;
  const nose = lm[0];
  const lSh = lm[11], rSh = lm[12];
  const lHip = lm[23], rHip = lm[24];

  // Need at least shoulders + hips to validate; skip if we can't see torso
  if (!lSh || !rSh || !lHip || !rHip) return true;
  const shVisOk = (lSh.visibility || 0) > 0.5 && (rSh.visibility || 0) > 0.5;
  const hipVisOk = (lHip.visibility || 0) > 0.5 && (rHip.visibility || 0) > 0.5;
  if (!shVisOk || !hipVisOk) return true; // not enough data — don't reject

  const shoulderY = (lSh.y + rSh.y) / 2;
  const hipY = (lHip.y + rHip.y) / 2;

  // Shoulders MUST be above hips in upright postures. Allow small tolerance.
  if (shoulderY > hipY + 0.08) return false;

  // Nose should be above shoulders (if visible)
  if (nose && (nose.visibility || 0) > 0.5) {
    if (nose.y > shoulderY + 0.12) return false;
  }

  // Torso width sanity
  const shoulderSpread = Math.abs(lSh.x - rSh.x);
  const hipSpread = Math.abs(lHip.x - rHip.x);
  if (shoulderSpread < 0.02 && hipSpread < 0.02) return false;

  return true;
};

// Detect left-right swap: MediaPipe sometimes flips L/R landmarks during fast motion.
// If BOTH shoulders AND BOTH hips simultaneously flip their relative X order vs previous frame, it's a swap.
const hasLeftRightFlip = (raw, prev) => {
  if (!prev || prev.length !== raw.length) return false;
  const lSh = raw[11], rSh = raw[12], lHip = raw[23], rHip = raw[24];
  const pLSh = prev[11], pRSh = prev[12], pLHip = prev[23], pRHip = prev[24];
  if (!lSh || !rSh || !lHip || !rHip || !pLSh || !pRSh || !pLHip || !pRHip) return false;
  if ((lSh.visibility || 0) < 0.5 || (rSh.visibility || 0) < 0.5) return false;
  if ((pLSh.visibility || 0) < 0.5 || (pRSh.visibility || 0) < 0.5) return false;

  const shOrderNow = Math.sign(lSh.x - rSh.x);
  const shOrderPrev = Math.sign(pLSh.x - pRSh.x);
  const hipOrderNow = Math.sign(lHip.x - rHip.x);
  const hipOrderPrev = Math.sign(pLHip.x - pRHip.x);

  // Both shoulder AND hip L/R order flipped = swap
  return shOrderNow !== 0 && shOrderPrev !== 0 &&
         shOrderNow !== shOrderPrev && hipOrderNow !== hipOrderPrev;
};

// Big-jump rejection
const hasImpossibleJump = (raw, prev) => {
  if (!prev || prev.length !== raw.length) return false;
  const JUMP_THRESHOLD = 0.4;
  for (let i = 0; i < raw.length; i++) {
    const r = raw[i], p = prev[i];
    if (!r || !p) continue;
    if ((r.visibility || 0) < 0.5 || (p.visibility || 0) < 0.5) continue;
    const dx = Math.abs(r.x - p.x);
    const dy = Math.abs(r.y - p.y);
    if (dx > JUMP_THRESHOLD || dy > JUMP_THRESHOLD) return true;
  }
  return false;
};

// ===================== LANDMARK SMOOTHING =====================
// Per-landmark One Euro Filter for per-joint velocity-aware smoothing.
// Reference params match Google MediaPipe's own smoothing defaults.
// NO jump rejection — just velocity-aware low-pass per axis per landmark.

// Google MediaPipe reference tuning for pose smoothing (from mediapipe/calculators/util/landmarks_smoothing_calculator.proto)
// min_cutoff: low baseline = heavy smoothing when still
// beta: high velocity response = quick tracking when moving (no lag)
const OE_MIN_CUTOFF = 0.5;
const OE_BETA = 40.0;
const OE_DCUTOFF = 1.0;

const oeAlpha = (cutoff, dt) => {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dt);
};

// One filter state per landmark index per axis
// Shape: { [idx]: { x:{xPrev,dxPrev,tPrev}, y:{...}, z:{...}, vis:number } }
const createOEState = () => ({});

const applyOEFilter = (state, idx, axis, value, now) => {
  if (!state[idx]) state[idx] = {};
  const key = axis;
  if (!state[idx][key]) {
    state[idx][key] = { xPrev: value, dxPrev: 0, tPrev: now };
    return value;
  }
  const s = state[idx][key];
  const dt = Math.max(0.001, (now - s.tPrev) / 1000); // seconds
  // Derivative (raw velocity)
  const dx = (value - s.xPrev) / dt;
  const aD = oeAlpha(OE_DCUTOFF, dt);
  const dxHat = aD * dx + (1 - aD) * s.dxPrev;
  // Adaptive cutoff: faster motion → higher cutoff → less smoothing (responsive)
  const cutoff = OE_MIN_CUTOFF + OE_BETA * Math.abs(dxHat);
  const a = oeAlpha(cutoff, dt);
  const xHat = a * value + (1 - a) * s.xPrev;
  s.xPrev = xHat;
  s.dxPrev = dxHat;
  s.tPrev = now;
  return xHat;
};

const smoothLandmarksOE = (raw, state, now) => {
  if (!raw) return raw;
  return raw.map((r, i) => {
    if (!r) return r;
    const vis = r.visibility || 0;
    const x = applyOEFilter(state, i, 'x', r.x, now);
    const y = applyOEFilter(state, i, 'y', r.y, now);
    const z = applyOEFilter(state, i, 'z', r.z || 0, now);
    // Smooth visibility too so joints fade in/out instead of blinking
    if (!state[i]) state[i] = {};
    const prevVis = state[i]._vis !== undefined ? state[i]._vis : vis;
    const smoothedVis = 0.25 * vis + 0.75 * prevVis;
    state[i]._vis = smoothedVis;
    return { x, y, z, visibility: smoothedVis, rawVisibility: vis };
  });
};

// ===================== ANGLE MATH =====================
const calcAngle2D = (a, b, c) => {
  if (!a || !b || !c) return null;
  const rad = Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
  let deg = Math.abs(rad * 180 / Math.PI);
  if (deg > 180) deg = 360 - deg;
  return deg;
};

const calcAngle3D = (a, b, c) => {
  if (!a || !b || !c) return null;
  const ba = { x: a.x - b.x, y: a.y - b.y, z: (a.z || 0) - (b.z || 0) };
  const bc = { x: c.x - b.x, y: c.y - b.y, z: (c.z || 0) - (b.z || 0) };
  const dot = ba.x * bc.x + ba.y * bc.y + ba.z * bc.z;
  const m1 = Math.sqrt(ba.x ** 2 + ba.y ** 2 + ba.z ** 2);
  const m2 = Math.sqrt(bc.x ** 2 + bc.y ** 2 + bc.z ** 2);
  if (m1 === 0 || m2 === 0) return null;
  return Math.acos(Math.max(-1, Math.min(1, dot / (m1 * m2)))) * (180 / Math.PI);
};

// ===================== UNIFIED ANALYZER =====================
const GOOD = '#00FF66', WARN = '#FFB800', DANGER = '#FF3333';

// Mirror map: left <-> right landmark indices
const MIRROR_MAP = { 11: 12, 12: 11, 13: 14, 14: 13, 15: 16, 16: 15, 23: 24, 24: 23, 25: 26, 26: 25, 27: 28, 28: 27, 29: 30, 30: 29, 31: 32, 32: 31 };

// Fallback rep thresholds
export const REP_THRESHOLDS = {
  squat: { startDescending: 35, deepThreshold: 50, peakMinimum: 45, resetThreshold: 25, abortThreshold: 20, cooldownMs: 800 },
  deadlift: { startDescending: 30, deepThreshold: 45, peakMinimum: 40, resetThreshold: 20, abortThreshold: 15, cooldownMs: 1000 },
  bench: { startDescending: 45, deepThreshold: 60, peakMinimum: 55, resetThreshold: 20, abortThreshold: 30, cooldownMs: 1200 },
};

export const getRepThresholds = (exerciseName, definition = null) => {
  if (definition && definition.rep_thresholds) {
    return {
      startDescending: definition.rep_thresholds.startDescending || 35,
      deepThreshold: definition.rep_thresholds.deepThreshold || 50,
      peakMinimum: definition.rep_thresholds.peakMinimum || 45,
      resetThreshold: definition.rep_thresholds.resetThreshold || 25,
      abortThreshold: definition.rep_thresholds.abortThreshold || 20,
      cooldownMs: definition.rep_thresholds.cooldownMs || 800,
    };
  }
  const e = (exerciseName || '').toLowerCase();
  if (e.includes('bench') || e.includes('press') || e.includes('push')) return REP_THRESHOLDS.bench;
  if (e.includes('deadlift') || e.includes('rdl') || e.includes('romanian')) return REP_THRESHOLDS.deadlift;
  return REP_THRESHOLDS.squat;
};

// Derive required visibility from definition landmarks
const getRequiredVisibility = (definition) => {
  // Use explicit required_landmarks if provided by the definition
  if (definition?.required_landmarks && definition.required_landmarks.length > 0) {
    const rl = new Set(definition.required_landmarks);
    return {
      needsUpper: [11, 12, 13, 14, 15, 16].some(i => rl.has(i)),
      needsLower: [25, 26, 27, 28].some(i => rl.has(i)),
      needsHips: [23, 24].some(i => rl.has(i)),
    };
  }
  // Fallback: derive from checkpoint and rep detection landmarks
  const allLandmarks = new Set();
  for (const cp of (definition?.form_checkpoints || [])) {
    for (const idx of (cp.landmark_triplet || [])) allLandmarks.add(idx);
    for (const idx of (cp.landmark_triplet_alt || [])) allLandmarks.add(idx);
  }
  for (const idx of (definition?.rep_detection?.primary_landmarks || [])) allLandmarks.add(idx);
  for (const idx of (definition?.rep_detection?.primary_landmarks_alt || [])) allLandmarks.add(idx);

  return {
    needsUpper: [11, 12, 13, 14, 15, 16].some(i => allLandmarks.has(i)),
    needsLower: [25, 26, 27, 28].some(i => allLandmarks.has(i)),
    needsHips: [23, 24].some(i => allLandmarks.has(i)),
  };
};

const checkRequiredParts = (vis, definition) => {
  const req = getRequiredVisibility(definition);
  const missing = [];
  if (req.needsUpper && !vis.shoulders) missing.push('Shoulders');
  if (req.needsHips && !vis.hips) missing.push('Hips');
  if (req.needsLower && !vis.knees) missing.push('Knees');
  return missing;
};

const hasRequiredVisibility = (vis, definition) => {
  const req = getRequiredVisibility(definition);
  if (req.needsUpper && !vis.shoulders) return false;
  if (req.needsHips && !vis.hips) return false;
  if (req.needsLower && !vis.knees) return false;
  return true;
};

// Single source of truth for view eligibility. Imported from the registry
// (frontend) which is mirrored byte-for-byte on the backend.
import { isEligibleForView as registryEligible, lookupMetric } from '../lib/metricsRegistry';

// Auto-detect camera view with stability buffer and manual override support.
// analyzeGeneric._viewOverride: null | 'front' | 'side' (manual override)
// analyzeGeneric._viewBuffer: last N raw detections for hysteresis.
const VIEW_BUFFER_SIZE = 20;

const detectCameraViewRaw = (lm) => {
  if (!lm || lm.length < 33) return 'unknown';
  const lShoulder = lm[11];
  const rShoulder = lm[12];
  const lHip = lm[23];
  const rHip = lm[24];

  const lsVis = lShoulder?.visibility || 0;
  const rsVis = rShoulder?.visibility || 0;
  const lhVis = lHip?.visibility || 0;
  const rhVis = rHip?.visibility || 0;

  const bothShouldersVisible = lsVis > 0.5 && rsVis > 0.5;
  const bothHipsVisible = lhVis > 0.5 && rhVis > 0.5;

  if (bothShouldersVisible && bothHipsVisible) {
    // Shoulder spread normalized by shoulder-hip distance (camera distance invariant)
    const shoulderSpread = Math.abs(lShoulder.x - rShoulder.x);
    const torsoLen = Math.abs(((lShoulder.y + rShoulder.y) / 2) - ((lHip.y + rHip.y) / 2));
    const ratio = torsoLen > 0.01 ? shoulderSpread / torsoLen : shoulderSpread;
    // ratio > ~0.6 → front view. ratio < ~0.35 → side view
    if (ratio > 0.55) return 'front';
    if (ratio < 0.3) return 'side';
    return 'angled';
  }
  if ((lsVis > 0.5 && rsVis < 0.3) || (rsVis > 0.5 && lsVis < 0.3)) return 'side';
  return 'front';
};

const detectCameraView = (lm) => {
  // Manual override takes priority
  if (analyzeGeneric._viewOverride === 'front' || analyzeGeneric._viewOverride === 'side') {
    return analyzeGeneric._viewOverride;
  }
  const raw = detectCameraViewRaw(lm);
  if (!analyzeGeneric._viewBuffer) analyzeGeneric._viewBuffer = [];
  analyzeGeneric._viewBuffer.push(raw);
  if (analyzeGeneric._viewBuffer.length > VIEW_BUFFER_SIZE) analyzeGeneric._viewBuffer.shift();
  // Majority vote for stability (prevents flicker between front/side/angled)
  const counts = { front: 0, side: 0, angled: 0, unknown: 0 };
  for (const v of analyzeGeneric._viewBuffer) counts[v] = (counts[v] || 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0][0];
};

// Single unified analyzer — 3D angle-driven, definition-driven, camera-independent
const analyzeGeneric = (lm2D, lm3D, definition) => {
  const vis = checkBodyVisibility(lm2D);
  const requiredParts = checkRequiredParts(vis, definition);
  const canAnalyze = hasRequiredVisibility(vis, definition);
  const cameraView = detectCameraView(lm2D);

  // ========== SETUP CHECK (3D spatial conditions — is body in correct position?) ==========
  let setupCheckPassed = true;
  const setupChecks = definition.setup_check || [];
  if (setupChecks.length > 0 && lm3D) {
    for (const check of setupChecks) {
      const a = lm3D[check.landmark_a];
      const b = lm3D[check.landmark_b];
      if (!a || !b) continue;
      const valA = a[check.axis || 'y'];
      const valB = b[check.axis || 'y'];
      const diff = Math.abs(valA - valB);
      if (check.condition === 'abs_diff_less_than' && diff >= (check.threshold || 0.25)) setupCheckPassed = false;
      if (check.condition === 'abs_diff_greater_than' && diff <= (check.threshold || 0.20)) setupCheckPassed = false;
    }
  }

  // ========== PRIMARY MOTION (3D angle-driven rep detection) ==========
  const pm = definition.primary_motion || {};
  const pmTriplet = pm.landmark_triplet || null;
  const pmTripletAlt = pm.landmark_triplet_alt || null;
  const pmStartRange = pm.start_range || [140, 180];
  const pmBottomRange = pm.bottom_range || [50, 120];
  const pmMinAmp = pm.min_amplitude || 35;
  // Movement direction (camera/exercise agnostic): +1 = flexion-first (angle
  // DECREASES from start to bottom, e.g. squat/pushup), -1 = extension-first
  // (angle INCREASES from start to bottom, e.g. hip thrust, glute bridge, bird
  // dog, prone raises). The rep state machine uses this so it counts reps for
  // BOTH directions instead of only "down-then-up" movements.
  const pmStartMid = (pmStartRange[0] + pmStartRange[1]) / 2;
  const pmBottomMid = (pmBottomRange[0] + pmBottomRange[1]) / 2;
  const pmDir = pmStartMid >= pmBottomMid ? 1 : -1;

  let pmAngle = null;
  if (pmTriplet) {
    // Pick the WORKING side rather than averaging L+R. Averaging breaks rep
    // counting for single-arm work (row, single-arm curl) and for ANY lift
    // filmed from the side, where the far limb is occluded and feeds garbage
    // into the mean — that's why "only one side counted". We instead take the
    // side that is FURTHER into the rep (more progress toward the bottom), so
    // the moving limb always drives the count regardless of camera angle/side.
    // `progress = pmDir * (startMid - angle)` → larger = deeper into the rep.
    const pickWorking = (a, b) => {
      if (a === null) return b;
      if (b === null) return a;
      const pa = pmDir * (pmStartMid - a);
      const pb = pmDir * (pmStartMid - b);
      return pa >= pb ? a : b;
    };
    // Try 3D first (precise). If depth-stability rejects 3D, fall back to 2D
    // for rep-counting — without this, FRONT-view rep counting fails because the
    // elbow/knee depth axis swings wildly and `safeAngle3D` returns null. 2D is
    // less accurate for metrics but fully sufficient for cycle detection.
    if (lm3D) {
      const a = safeAngle3D(lm2D, lm3D, pmTriplet[0], pmTriplet[1], pmTriplet[2]);
      const b = pmTripletAlt ? safeAngle3D(lm2D, lm3D, pmTripletAlt[0], pmTripletAlt[1], pmTripletAlt[2]) : null;
      pmAngle = pickWorking(a, b);
    }
    if (pmAngle === null) {
      // 2D fallback (camera-plane angle) — still good enough for cycle detection.
      const a = safeAngle(lm2D, pmTriplet[0], pmTriplet[1], pmTriplet[2], calcAngle2D);
      const b = pmTripletAlt ? safeAngle(lm2D, pmTripletAlt[0], pmTripletAlt[1], pmTripletAlt[2], calcAngle2D) : null;
      pmAngle = pickWorking(a, b);
    }
  }

  // 5-frame rolling average on primary motion angle
  if (pmAngle !== null) {
    if (!analyzeGeneric._pmBuf) analyzeGeneric._pmBuf = [];
    analyzeGeneric._pmBuf.push(pmAngle);
    if (analyzeGeneric._pmBuf.length > 5) analyzeGeneric._pmBuf.shift();
    pmAngle = analyzeGeneric._pmBuf.reduce((s, v) => s + v, 0) / analyzeGeneric._pmBuf.length;
  }

  // Angle velocity (degrees per frame, positive = extending, negative = flexing)
  let pmVelocity = 0;
  if (pmAngle !== null && analyzeGeneric._prevPm !== undefined && analyzeGeneric._prevPm !== null) {
    pmVelocity = pmAngle - analyzeGeneric._prevPm;
  }
  analyzeGeneric._prevPm = pmAngle;

  const inStartRange = pmAngle !== null && pmAngle >= pmStartRange[0] && pmAngle <= pmStartRange[1];
  const inBottomRange = pmAngle !== null && pmAngle >= pmBottomRange[0] && pmAngle <= pmBottomRange[1];

  // Depth% from angle (0% = start/top, 100% = bottom)
  let depthPct = 0;
  if (pmAngle !== null) {
    const startMid = (pmStartRange[0] + pmStartRange[1]) / 2;
    const bottomMid = (pmBottomRange[0] + pmBottomRange[1]) / 2;
    depthPct = Math.max(0, Math.min(100, ((startMid - pmAngle) / Math.max(1, startMid - bottomMid)) * 100));
  }

  // Symmetry from bilateral comparison
  let symmetry = null;
  if (pmTriplet && pmTripletAlt && lm3D) {
    const leftAngle = safeAngle3D(lm2D, lm3D, pmTriplet[0], pmTriplet[1], pmTriplet[2]);
    const rightAngle = safeAngle3D(lm2D, lm3D, pmTripletAlt[0], pmTripletAlt[1], pmTripletAlt[2]);
    if (leftAngle !== null && rightAngle !== null) symmetry = Math.max(0, 100 - Math.abs(leftAngle - rightAngle));
  }

  let phase = 'standing';
  if (depthPct > 55) phase = 'bottom';
  else if (depthPct > 25) phase = 'descending';

  // ========== FORM CHECKPOINTS (3D angles) ==========
  if (!analyzeGeneric._angleBuffers) analyzeGeneric._angleBuffers = {};
  const checkpointAngles = [];
  for (const cp of (definition.form_checkpoints || [])) {
    const [la, lb, lc] = cp.landmark_triplet || [];
    if (la === undefined) continue;
    const cpView = cp.view || 'both';
    // Registry-driven view eligibility — single source of truth. The
    // checkpoint's `view` hint is honored when present, else we look up the
    // label in the registry. `cp.view='both'` always matches; unknown
    // metrics are permissive (so Claude-generated checkpoints flow through).
    let viewMatch;
    if (cpView === 'both') {
      viewMatch = true;
    } else if (cpView && cpView !== 'unknown') {
      // Explicit hint on the checkpoint — exact-match or angled-can-fall-back-to-front.
      if (cpView === cameraView) viewMatch = true;
      else if (cameraView === 'angled' && cpView === 'front') viewMatch = true;
      else viewMatch = false;
    } else {
      viewMatch = registryEligible(cp.label, cameraView);
    }
    let angle = lm3D ? safeAngle3D(lm2D, lm3D, la, lb, lc) : safeAngle(lm2D, la, lb, lc, calcAngle2D);
    if (angle === null && cp.landmark_triplet_alt) {
      const [aa, ab, ac] = cp.landmark_triplet_alt;
      angle = lm3D ? safeAngle3D(lm2D, lm3D, aa, ab, ac) : safeAngle(lm2D, aa, ab, ac, calcAngle2D);
    }
    if (angle !== null) {
      // 5-frame MEDIAN (not mean) — a single bad frame in 5 has zero impact on
      // the median, but sustained drift still tracks. This is the per-frame
      // outlier rejector that the old moving-average lacked.
      const bufKey = `${la}_${lb}_${lc}`;
      if (!analyzeGeneric._angleBuffers[bufKey]) analyzeGeneric._angleBuffers[bufKey] = [];
      analyzeGeneric._angleBuffers[bufKey].push(angle);
      if (analyzeGeneric._angleBuffers[bufKey].length > 5) analyzeGeneric._angleBuffers[bufKey].shift();
      angle = median(analyzeGeneric._angleBuffers[bufKey]);
    }
    checkpointAngles.push({ ...cp, angle, jointIdx: lb, viewMatch });
  }

  const checkpoints = [];
  const quality = {};
  if (!analyzeGeneric._checkpointStates) analyzeGeneric._checkpointStates = {};
  if (canAnalyze && phase !== 'standing') {
    for (const ca of checkpointAngles) {
      if (ca.angle === null || !ca.viewMatch) continue;
      const evaluateAt = ca.evaluate_at || 'always';
      if (evaluateAt === 'bottom' && depthPct < 55) continue;
      if (evaluateAt === 'top' && depthPct > 20) continue;

      // Evaluate BOTH sides — use worse status. Alt side gets the same
      // 5-frame median treatment so a glitch on one frame can't dominate.
      let altAngle = null;
      if (ca.landmark_triplet_alt && lm3D) {
        const [aa, ab, ac] = ca.landmark_triplet_alt;
        altAngle = safeAngle3D(lm2D, lm3D, aa, ab, ac);
        if (altAngle !== null) {
          const abk = `${aa}_${ab}_${ac}`;
          if (!analyzeGeneric._angleBuffers[abk]) analyzeGeneric._angleBuffers[abk] = [];
          analyzeGeneric._angleBuffers[abk].push(altAngle);
          if (analyzeGeneric._angleBuffers[abk].length > 5) analyzeGeneric._angleBuffers[abk].shift();
          altAngle = median(analyzeGeneric._angleBuffers[abk]);
        }
      }

      const [lo, hi] = ca.ideal_range || [80, 100];
      const stateKey = `${(ca.landmark_triplet || []).join('_')}`;
      const prevStatus = analyzeGeneric._checkpointStates[stateKey] || 'good';

      // Hysteresis: requires significant deviation to change state (prevents flickering)
      const evalWithHysteresis = (a) => {
        if (a === null) return prevStatus;
        if (prevStatus === 'good') {
          if (a < lo - 12 || a > hi + 12) return 'danger';
          if (a < lo - 5 || a > hi + 5) return 'warning';
          return 'good';
        } else if (prevStatus === 'warning') {
          if (a >= lo + 3 && a <= hi - 3) return 'good';
          if (a < lo - 12 || a > hi + 12) return 'danger';
          return 'warning';
        } else {
          if (a >= lo + 3 && a <= hi - 3) return 'good';
          if (a >= lo - 5 && a <= hi + 5) return 'warning';
          return 'danger';
        }
      };

      const primaryStatus = evalWithHysteresis(ca.angle);
      const altStatus = altAngle !== null ? evalWithHysteresis(altAngle) : primaryStatus;
      const statusRank = { good: 0, warning: 1, danger: 2 };
      const status = statusRank[primaryStatus] >= statusRank[altStatus] ? primaryStatus : altStatus;
      analyzeGeneric._checkpointStates[stateKey] = status;

      const tip = status === 'good' ? (ca.tip_good || 'Good form') : (ca.tip_bad || 'Watch your form');
      const color = status === 'good' ? GOOD : status === 'warning' ? WARN : DANGER;
      quality[ca.jointIdx] = color;
      if (ca.landmark_triplet_alt) quality[ca.landmark_triplet_alt[1]] = color;
      checkpoints.push({ label: ca.label, value: `${ca.angle.toFixed(0)}°`, status, tip });
    }
  }
  if (phase === 'standing') {
    for (const ca of checkpointAngles) { if (ca.jointIdx !== undefined) quality[ca.jointIdx] = GOOD; }
  }

  // ========== CONSTRAINTS DURING REP ==========
  let constraintsOk = true;
  const constraintViolations = [];
  const movId = definition.movement_identity || {};
  if (phase !== 'standing') {
    for (const ct of (movId.constraints_during_rep || [])) {
      const [a, b, c] = ct.landmark_triplet || [];
      if (a === undefined) continue;
      let angle = lm3D ? safeAngle3D(lm2D, lm3D, a, b, c) : safeAngle(lm2D, a, b, c, calcAngle2D);
      if (angle === null && ct.landmark_triplet_alt) {
        const [aa, ab, ac] = ct.landmark_triplet_alt;
        angle = lm3D ? safeAngle3D(lm2D, lm3D, aa, ab, ac) : safeAngle(lm2D, aa, ab, ac, calcAngle2D);
      }
      if (angle === null) continue;
      const [lo, hi] = ct.range || [0, 360];
      if (angle < lo || angle > hi) { constraintsOk = false; constraintViolations.push(ct.violation || ct.label); }
    }
  }

  // ========== TOP POSITION (must return here to complete a rep) ==========
  let topPositionMet = true;
  for (const tp of (movId.top_position || [])) {
    const [a, b, c] = tp.landmark_triplet || [];
    if (a === undefined) continue;
    let angle = lm3D ? safeAngle3D(lm2D, lm3D, a, b, c) : safeAngle(lm2D, a, b, c, calcAngle2D);
    if (angle === null && tp.landmark_triplet_alt) {
      const [aa, ab, ac] = tp.landmark_triplet_alt;
      angle = lm3D ? safeAngle3D(lm2D, lm3D, aa, ab, ac) : safeAngle(lm2D, aa, ab, ac, calcAngle2D);
    }
    if (angle === null) continue;
    const [lo, hi] = tp.range || [0, 360];
    if (angle < lo - 10 || angle > hi + 10) topPositionMet = false;
  }

  // ========== FORM LABELS ==========
  const formLabels = (definition.form_labels || []).map((fl, i) => {
    const cpIdx = fl.checkpoint_index !== undefined ? fl.checkpoint_index : i;
    const ca = checkpointAngles[cpIdx];
    if (!ca || !ca.viewMatch || ca.angle === null) return { bodyPart: fl.body_part, label: '--', color: '#A1A1AA' };
    const [lo, hi] = ca.ideal_range || [80, 100];
    if (phase === 'standing') return { bodyPart: fl.body_part, label: fl.good_label || 'Ready', color: GOOD };
    if (ca.angle >= lo && ca.angle <= hi) return { bodyPart: fl.body_part, label: fl.good_label || 'Good', color: GOOD };
    if (ca.angle >= lo - 20 && ca.angle <= hi + 20) return { bodyPart: fl.body_part, label: fl.bad_label || 'Watch', color: WARN };
    return { bodyPart: fl.body_part, label: fl.bad_label || 'Fix', color: DANGER };
  });

  const displayAngles = formLabels.length > 0 ? [] : checkpointAngles.filter(ca => ca.viewMatch).map(ca => ({
    label: ca.label, value: ca.angle !== null ? ca.angle.toFixed(0) + '°' : '--', color: quality[ca.jointIdx] || GOOD,
  }));

  // Exercise-specific joint angles dict: { "Elbow Bend": 95, "Hip Line": 172 }.
  // Populated from the checkpoint angles the DEFINITION asked us to track, not from
  // hardcoded joints. This is what gets stored per-rep and aggregated into
  // session.summary.joint_angles — no knee/hip garbage on bicep curls.
  const jointAngles = {};
  const pmLabel = (definition.primary_motion && definition.primary_motion.label) || null;
  if (pmLabel && pmAngle !== null && !isNaN(pmAngle)) {
    jointAngles[pmLabel] = Math.round(pmAngle);
  }
  for (const ca of checkpointAngles) {
    if (!ca.viewMatch || ca.angle === null) continue;
    const label = ca.label;
    if (!label || jointAngles[label] !== undefined) continue;
    jointAngles[label] = Math.round(ca.angle);
  }

  // ========== PHASE A.7 — VIEW-AWARE FRONT METRICS ==========
  // When camera is FRONT, append 2D-only metrics that actually work from the
  // front (symmetry, elbow flare, shoulder tilt, knee valgus, head alignment).
  // These complement (don't replace) the 3D side-view metrics — both flow
  // into the same jointAngles dict, distinguished by name. Backend stores
  // them as-is and the report card renders them with a `FRONT` view pill.
  //
  // Selection per exercise category — based on exercise name keywords. The
  // long tail goes through the AI generator's v2 schema once that ships;
  // for now this category-detect covers the most common moves.
  if (cameraView === 'front' && lm2D && lm2D.length >= 33) {
    const exName = (definition.name || '').toLowerCase();
    const isPushup = /push.?up|press|burpee/.test(exName);
    // Knee valgus is a BILATERAL knee-vs-ankle x-spread ratio — it is ONLY valid
    // for square-stance squats. Lunges/split-squats (staggered stance) and
    // deadlifts (hip hinge) make that ratio meaningless, so they must NOT get a
    // valgus reading. They keep the universal shoulder-symmetry / tilt metrics.
    const isSquat = /squat/.test(exName) && !/lunge|split/.test(exName);
    const isPlank = /plank|hold/.test(exName);

    // Universal: shoulder symmetry — applies to almost everything
    const shSym = calcSymmetryY(lm2D, 11, 12);
    if (shSym !== null) jointAngles['Shoulder Symmetry'] = shSym;

    if (isPushup) {
      // Elbow flare (use whichever side is more visible)
      const flareL = calcElbowFlare(lm2D, 11, 13);
      const flareR = calcElbowFlare(lm2D, 12, 14);
      const flares = [flareL, flareR].filter(x => x !== null);
      if (flares.length) jointAngles['Elbow Flare'] = Math.round(flares.reduce((a, b) => a + b, 0) / flares.length);
      const head = calcHeadAlignment(lm2D);
      if (head !== null) jointAngles['Head Alignment'] = head;
    } else if (isSquat) {
      const valgus = calcKneeValgus(lm2D);
      if (valgus !== null) jointAngles['Knee Valgus'] = valgus;
      // Hip symmetry
      const hipSym = calcSymmetryY(lm2D, 23, 24);
      if (hipSym !== null) jointAngles['Hip Symmetry'] = hipSym;
    } else if (isPlank) {
      const head = calcHeadAlignment(lm2D);
      if (head !== null) jointAngles['Head Alignment'] = head;
      const hipSym = calcSymmetryY(lm2D, 23, 24);
      if (hipSym !== null) jointAngles['Hip Symmetry'] = hipSym;
    }

    // Shoulder tilt is universally useful from front
    const tilt = calcShoulderTilt(lm2D);
    if (tilt !== null) jointAngles['Shoulder Tilt'] = tilt;
  }

  const formEngagedCount = checkpoints.filter(c => c.status === 'good' || c.status === 'warning').length;

  return {
    phase, depthPct, checkpoints, quality, displayAngles, formLabels, jointAngles,
    idealAngles: definition.ideal_angles || {},
    symmetry, bodyVisibility: vis, missingParts: requiredParts,
    cameraView, repValid: true, formEngagedCount, constraintsOk, constraintViolations,
    raw: { primaryAngle: pmAngle, mirrorAngle: null, bestAngle: pmAngle },
    primaryMotion: { angle: pmAngle, velocity: pmVelocity, dir: pmDir, inStartRange, inBottomRange, depthPct, minAmplitude: pmMinAmp },
    setupCheckPassed, topPositionMet,
  };
};


// Minimal fallback when no AI definition exists
const FALLBACK_DEFINITION = {
  form_checkpoints: [
    { label: 'L Hip', landmark_triplet: [11, 23, 25], ideal_range: [60, 170], tip_good: 'Good', tip_bad: 'Watch form' },
    { label: 'R Hip', landmark_triplet: [12, 24, 26], ideal_range: [60, 170], tip_good: 'Good', tip_bad: 'Watch form' },
    { label: 'L Knee', landmark_triplet: [23, 25, 27], ideal_range: [60, 170], tip_good: 'Good', tip_bad: 'Watch form' },
    { label: 'R Knee', landmark_triplet: [24, 26, 28], ideal_range: [60, 170], tip_good: 'Good', tip_bad: 'Watch form' },
  ],
  rep_detection: { primary_landmarks: [23, 25, 27] },
  rep_thresholds: REP_THRESHOLDS.squat,
  primary_motion: {
    landmark_triplet: [23, 25, 27],
    landmark_triplet_alt: [24, 26, 28],
    start_range: [150, 180],
    bottom_range: [60, 120],
    min_amplitude: 30,
    label: 'Primary motion',
  },
  ideal_angles: {},
};

export const setCameraViewOverride = (view) => {
  // view: 'front' | 'side' | null (null = auto-detect)
  analyzeGeneric._viewOverride = (view === 'front' || view === 'side') ? view : null;
  analyzeGeneric._viewBuffer = [];
};

// Get list of trackable metrics for the active camera view based on the definition
export const getTrackableMetrics = (definition, cameraView) => {
  if (!definition) return [];
  const seen = new Set();
  const metrics = [];
  const viewCompatible = (v) => !v || v === 'both' || v === cameraView || cameraView === 'angled' || cameraView === 'unknown';
  // Primary motion is always tracked (rep counting works from any view with visible joints)
  if (definition.primary_motion?.label) {
    metrics.push({ label: definition.primary_motion.label, primary: true });
    seen.add(definition.primary_motion.label.toLowerCase());
  } else {
    metrics.push({ label: 'Reps', primary: true });
    seen.add('reps');
  }
  for (const cp of (definition.form_checkpoints || [])) {
    if (!viewCompatible(cp.view)) continue;
    const key = (cp.label || '').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    metrics.push({ label: cp.label, primary: false });
  }
  return metrics;
};

export const analyzeExerciseMovement = (exerciseName, lm2D, lm3D = null, definition = null) => {
  if (!lm2D || lm2D.length < 33) return null;
  const def = (definition && definition.form_checkpoints && definition.form_checkpoints.length > 0)
    ? definition : FALLBACK_DEFINITION;
  if (analyzeGeneric._lastExercise !== exerciseName) {
    analyzeGeneric._angleBuffers = {};
    analyzeGeneric._pmBuf = [];
    analyzeGeneric._prevPm = null;
    analyzeGeneric._checkpointStates = {};
    analyzeGeneric._viewBuffer = [];
    analyzeGeneric._lastExercise = exerciseName;
  }
  return analyzeGeneric(lm2D, lm3D, def);
};

export const calculateJointAngles = (landmarks, use3D = false) => {
  if (!landmarks || landmarks.length < 33) return null;
  const c = use3D ? calcAngle3D : calcAngle2D;
  const L = POSE_LANDMARKS;
  return {
    leftKnee: safeAngle(landmarks, L.LEFT_HIP, L.LEFT_KNEE, L.LEFT_ANKLE, c),
    leftHip: safeAngle(landmarks, L.LEFT_SHOULDER, L.LEFT_HIP, L.LEFT_KNEE, c),
    leftElbow: safeAngle(landmarks, L.LEFT_SHOULDER, L.LEFT_ELBOW, L.LEFT_WRIST, c),
    rightKnee: safeAngle(landmarks, L.RIGHT_HIP, L.RIGHT_KNEE, L.RIGHT_ANKLE, c),
    rightHip: safeAngle(landmarks, L.RIGHT_SHOULDER, L.RIGHT_HIP, L.RIGHT_KNEE, c),
    rightElbow: safeAngle(landmarks, L.RIGHT_SHOULDER, L.RIGHT_ELBOW, L.RIGHT_WRIST, c),
    spineAngle: safeAngle(landmarks, L.LEFT_HIP, L.LEFT_SHOULDER, L.NOSE, c),
  };
};

export const calculateBodyProportions = () => null;
export const generateIdealPose = () => null;
export const evaluateFormQuality = () => ({});

// ==================== SKELETON DRAWING =====================
const SKELETON_COLOR = '#00FF66';
const JOINT_SIZE = 12;
const LINE_WIDTH = 4;

// Trust MediaPipe's prediction — always draw every landmark it returns.
// Suppression only at extreme frame edges (see per-call checks below).
// Using variable opacity used to cause the "flicker/drop" feel when an arm
// was momentarily occluded by the body, even though MediaPipe still predicted
// a valid position. We now keep the skeleton whole at all times.
const visToAlpha = () => 1;

const drawGreenSkeleton = (ctx, landmarks, quality) => {
  if (!landmarks || !ctx) return;
  const w = ctx.canvas.width, h = ctx.canvas.height;
  const toPixel = (l) => ({ x: l.x * w, y: l.y * h });

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const [s, e] of POSE_CONNECTIONS) {
    const sl = landmarks[s], el = landmarks[e];
    if (!sl || !el) continue;
    const sV = sl.visibility || 0, eV = el.visibility || 0;
    const lineAlpha = Math.min(visToAlpha(sV), visToAlpha(eV));
    if (lineAlpha <= 0) continue;
    // Skip landmarks near frame edges (MediaPipe guesses wrong positions for off-screen body parts)
    if (sl.y > 0.97 || sl.y < 0.02 || el.y > 0.97 || el.y < 0.02) continue;
    if (sl.x > 0.98 || sl.x < 0.02 || el.x > 0.98 || el.x < 0.02) continue;
    const p1 = toPixel(sl), p2 = toPixel(el);
    const lineColor = quality[s] || quality[e] || SKELETON_COLOR;
    ctx.globalAlpha = lineAlpha;
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = LINE_WIDTH;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  const joints = [11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
  for (const idx of joints) {
    const l = landmarks[idx];
    if (!l) continue;
    const a = visToAlpha(l.visibility || 0);
    if (a <= 0) continue;
    const p = toPixel(l);
    const color = quality[idx] || SKELETON_COLOR;
    ctx.globalAlpha = a;
    ctx.beginPath();
    ctx.arc(p.x, p.y, JOINT_SIZE, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(p.x, p.y, JOINT_SIZE, 0, Math.PI * 2);
    ctx.strokeStyle = '#00000088';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(p.x - JOINT_SIZE * 0.15, p.y - JOINT_SIZE * 0.15, JOINT_SIZE * 0.3, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF35';
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Head
  const nose = landmarks[0], lSh = landmarks[11], rSh = landmarks[12];
  if (nose && lSh && rSh) {
    const headAlpha = Math.min(visToAlpha(nose.visibility || 0), visToAlpha(lSh.visibility || 0), visToAlpha(rSh.visibility || 0));
    if (headAlpha > 0) {
      ctx.globalAlpha = headAlpha;
      const pNose = toPixel(nose);
      const neckX = (toPixel(lSh).x + toPixel(rSh).x) / 2;
      const neckY = (toPixel(lSh).y + toPixel(rSh).y) / 2;
      ctx.beginPath();
      ctx.moveTo(pNose.x, pNose.y);
      ctx.lineTo(neckX, neckY);
      ctx.strokeStyle = SKELETON_COLOR;
      ctx.lineWidth = LINE_WIDTH;
      ctx.lineCap = 'round';
      ctx.stroke();
      const headSize = JOINT_SIZE + 3;
      ctx.beginPath();
      ctx.arc(pNose.x, pNose.y, headSize, 0, Math.PI * 2);
      ctx.fillStyle = quality[0] || SKELETON_COLOR;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(pNose.x, pNose.y, headSize, 0, Math.PI * 2);
      ctx.strokeStyle = '#00000088';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }
};

// ==================== MAIN HOOK ====================
export const usePoseDetection3D = (enabled = true) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseLandmarkerRef = useRef(null);
  const animationRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [landmarks2D, setLandmarks2D] = useState(null);
  const [landmarks3D, setLandmarks3D] = useState(null);
  const [exerciseAnalysis, setExerciseAnalysis] = useState(null);

  const landmarks2DRef = useRef(null);
  const landmarks3DRef = useRef(null);
  const exerciseAnalysisRef = useRef(null);
  const exerciseNameRef = useRef('Squat');
  const exerciseDefinitionRef = useRef(null);
  const throttleTimerRef = useRef(null);
  const qualityColorsRef = useRef({});
  const smoothedLandmarksRef = useRef(null);
  const oeStateRef = useRef(createOEState());
  const rejectCountRef = useRef(0);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedFrames, setRecordedFrames] = useState([]);
  const recordingStartTime = useRef(null);
  const isRecordingRef = useRef(false);
  const recordedFramesRef = useRef([]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  // MediaRecorder for video capture
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [recordedVideoBlob, setRecordedVideoBlob] = useState(null);

  const setExerciseName = useCallback((name) => { exerciseNameRef.current = name; }, []);
  const setExerciseDefinition = useCallback((def) => { exerciseDefinitionRef.current = def; }, []);

  const startThrottle = useCallback(() => {
    if (throttleTimerRef.current) return;
    throttleTimerRef.current = setInterval(() => {
      setLandmarks2D(landmarks2DRef.current);
      setLandmarks3D(landmarks3DRef.current);
      setExerciseAnalysis(exerciseAnalysisRef.current);
      if (isRecordingRef.current) setRecordedFrames([...recordedFramesRef.current]);
    }, 100);
  }, []);

  const stopThrottle = useCallback(() => {
    if (throttleTimerRef.current) { clearInterval(throttleTimerRef.current); throttleTimerRef.current = null; }
  }, []);

  const initialize = useCallback(async () => {
    if (poseLandmarkerRef.current) return true;
    const MAX_RETRIES = 2;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        setIsLoading(true);
        setError(null);
        // Lazy-load MediaPipe so the main bundle stays small.
        const { FilesetResolver, PoseLandmarker } = await import('@mediapipe/tasks-vision');
        const vision = await FilesetResolver.forVisionTasks('https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm');
        poseLandmarkerRef.current = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numPoses: 1,
          minPoseDetectionConfidence: 0.6,
          minPosePresenceConfidence: 0.6,
          minTrackingConfidence: 0.6,
        });
        setIsLoading(false);
        return true;
      } catch (err) {
        console.error(`MediaPipe init attempt ${attempt + 1} failed:`, err);
        if (attempt === MAX_RETRIES) {
          setError(`Failed to load pose detection model. ${err.message || 'Check your connection and reload.'}`);
          setIsLoading(false);
          return false;
        }
        await new Promise(r => setTimeout(r, 1000));
      }
    }
    return false;
  }, []);

  const detectPose = useCallback(() => {
    if (!poseLandmarkerRef.current || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current, canvas = canvasRef.current;
    if (video.readyState < 2 || video.videoWidth === 0) { animationRef.current = requestAnimationFrame(detectPose); return; }
    const ctx = canvas.getContext('2d');
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) { canvas.width = video.videoWidth; canvas.height = video.videoHeight; }
    try {
      const results = poseLandmarkerRef.current.detectForVideo(video, performance.now());
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (results.landmarks && results.landmarks.length > 0) {
        const rawLm = results.landmarks[0];
        const lm3D = results.worldLandmarks?.[0] || null;

        // Sanity checks: reject inverted skeletons, L/R flips, and impossible teleports
        const prevSmooth = smoothedLandmarksRef.current;
        const sane = isSkeletonSane(rawLm);
        const flipped = hasLeftRightFlip(rawLm, prevSmooth);
        const teleported = hasImpossibleJump(rawLm, prevSmooth);
        const MAX_REJECT = 3;

        if ((!sane || flipped || teleported) && rejectCountRef.current < MAX_REJECT) {
          // Drop this frame. Reuse last good smoothed skeleton.
          rejectCountRef.current++;
          if (prevSmooth) {
            drawGreenSkeleton(ctx, prevSmooth, qualityColorsRef.current);
          }
          animationRef.current = requestAnimationFrame(detectPose);
          return;
        }

        // Accept frame: either sane or we've rejected too many in a row — let it resync
        if (rejectCountRef.current >= MAX_REJECT) {
          // Force resync: blow away smoothing state so we don't blend through a flipped skeleton
          oeStateRef.current = createOEState();
        }
        rejectCountRef.current = 0;

        const lm2D = smoothLandmarksOE(rawLm, oeStateRef.current, performance.now());
        smoothedLandmarksRef.current = lm2D;
        landmarks2DRef.current = lm2D;
        landmarks3DRef.current = lm3D;
        const analysis = analyzeExerciseMovement(exerciseNameRef.current, lm2D, lm3D, exerciseDefinitionRef.current);
        exerciseAnalysisRef.current = analysis;
        if (analysis) qualityColorsRef.current = analysis.quality;
        drawGreenSkeleton(ctx, lm2D, qualityColorsRef.current);
        if (isRecordingRef.current && lm3D) {
          recordedFramesRef.current.push({ timestamp: Date.now() - (recordingStartTime.current || Date.now()), landmarks3D: lm3D, landmarks2D: lm2D });
        }
      }
    } catch (err) { console.error('Detection error:', err); }
    animationRef.current = requestAnimationFrame(detectPose);
  }, []);

  const startCamera = useCallback(async (requestedFacingMode = 'user') => {
    let attempts = 0;
    while (!videoRef.current && attempts < 10) { await new Promise(r => setTimeout(r, 50)); attempts++; }
    if (!videoRef.current) { setError('Video element not available.'); return; }
    try {
      setIsLoading(true); setError(null);
      smoothedLandmarksRef.current = null;
      oeStateRef.current = createOEState();
      rejectCountRef.current = 0;
      if (!poseLandmarkerRef.current) { const ok = await initialize(); if (!ok) return; }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: requestedFacingMode }
      });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraActive(true); setIsLoading(false);
      detectPose(); startThrottle();
    } catch (err) {
      try {
        // Fallback: try the other facing mode
        const fallbackMode = requestedFacingMode === 'user' ? 'environment' : 'user';
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: fallbackMode } });
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true); setIsLoading(false);
        detectPose(); startThrottle();
      } catch (err2) {
        console.error('Camera error:', err2); setIsLoading(false);
        if (err2.name === 'NotAllowedError') setError('Camera access denied. Allow camera in browser settings.');
        else if (err2.name === 'NotFoundError') setError('No camera found.');
        else setError(`Camera error: ${err2.message}`);
      }
    }
  }, [initialize, detectPose, startThrottle]);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) { videoRef.current.srcObject.getTracks().forEach(t => t.stop()); videoRef.current.srcObject = null; }
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    stopThrottle(); setCameraActive(false);
    landmarks2DRef.current = null; landmarks3DRef.current = null;
    smoothedLandmarksRef.current = null;
    oeStateRef.current = createOEState();
    setLandmarks2D(null); setLandmarks3D(null); setExerciseAnalysis(null);
  }, [stopThrottle]);

  const startRecording = useCallback(() => {
    recordedFramesRef.current = [];
    setRecordedFrames([]);
    recordedChunksRef.current = [];
    setRecordedVideoBlob(null);
    recordingStartTime.current = Date.now();
    setIsRecording(true);

    // Start MediaRecorder on the video stream
    try {
      const stream = videoRef.current?.srcObject;
      if (stream) {
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : MediaRecorder.isTypeSupported('video/webm')
          ? 'video/webm'
          : 'video/mp4';
        const mr = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2500000 });
        mr.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
        mr.start(1000); // chunk every 1s
        mediaRecorderRef.current = mr;
      }
    } catch (err) {
      console.warn('MediaRecorder not available:', err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    const frames = [...recordedFramesRef.current];
    setRecordedFrames(frames);

    // Stop MediaRecorder and build video blob
    return new Promise((resolve) => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') {
        mr.onstop = () => {
          const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType || 'video/webm' });
          setRecordedVideoBlob(blob);
          mediaRecorderRef.current = null;
          resolve({ frames, videoBlob: blob });
        };
        mr.stop();
      } else {
        resolve({ frames, videoBlob: null });
      }
    });
  }, []);

  const clearRecording = useCallback(() => {
    recordedFramesRef.current = [];
    setRecordedFrames([]);
    recordedChunksRef.current = [];
    setRecordedVideoBlob(null);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
  }, []);

  useEffect(() => { return () => { stopCamera(); if (poseLandmarkerRef.current) poseLandmarkerRef.current.close(); }; }, [stopCamera]);

  return {
    videoRef, canvasRef, isLoading, error,
    landmarks2D, landmarks3D, exerciseAnalysis,
    cameraActive, startCamera, stopCamera, setExerciseName, setExerciseDefinition,
    isRecording, recordedFrames, recordedVideoBlob, startRecording, stopRecording, clearRecording
  };
};

export default usePoseDetection3D;
